import type {
  CycleHeaderCycle,
  CycleHeaderPhase,
  CycleType,
} from "./useCycles";
import type { Appointment } from "@src/packages/care/hooks/useAppointments";
import type { PatientMedication } from "@src/packages/care/hooks/usePatientMedications";
import { toLocalDateString } from "@src/packages/utils/date";
import type { PgtPlan } from "../presenters/resolveConditionalCycleGraph";
import {
  getCycleHeaderPhaseType,
  getCycleHeaderStepIds,
} from "../presenters/cycleHeaderSteps";
import { isCycleStepComplete } from "../presenters/phaseStepCompletion";

export type CycleHeaderState =
  | { kind: "no_cycle" }
  | { kind: "cycle_closed"; cycleType: CycleType }
  | { kind: "pre_treatment"; cycleType: CycleType; dayInPhase: number }
  | {
      kind: "stim_trigger_logged";
      cycleType: CycleType;
      triggerDate: string;
      triggerTime: string | null;
    }
  | {
      kind: "stim_monitoring_next";
      cycleType: CycleType;
      dayInPhase: number;
      apptDate: string;
      apptTime: string | null;
    }
  | {
      kind: "stim_med_next";
      cycleType: CycleType;
      dayInPhase: number;
      medDate: string;
      medTime: string;
    }
  | { kind: "stim_generic"; cycleType: CycleType; dayInPhase: number }
  | {
      kind: "retrieval_scheduled";
      cycleType: CycleType;
      retrievalDate: string;
    }
  | {
      kind: "retrieval_done_pending";
      cycleType: CycleType;
      retrievalDate: string | null;
    }
  | { kind: "fertilization"; cycleType: CycleType }
  | { kind: "embryo_dev"; cycleType: CycleType; dayInPhase: number }
  | { kind: "transfer_scheduled"; cycleType: CycleType; transferDate: string }
  | {
      kind: "beta_scheduled";
      cycleType: CycleType;
      betaDate: string;
      betaTime: string | null;
    }
  | { kind: "beta_pending"; cycleType: CycleType }
  /**
   * A result is recorded but the patient has not closed the cycle yet. Distinct
   * from `cycle_closed`, and distinct from `unknown_active`, which would tell
   * someone whose result is already in to keep logging their steps.
   */
  | { kind: "result_recorded"; cycleType: CycleType }
  | { kind: "unknown_active"; cycleType: CycleType };

export interface ResolveInput {
  cycle: CycleHeaderCycle | null | undefined;
  phases: CycleHeaderPhase[];
  appointments: Appointment[];
  medications: PatientMedication[];
  pgtPlan?: PgtPlan | null;
  cyclePlanSnapshot?: CycleHeaderCycle["cyclePlanSnapshot"];
  now: Date;
}

const MONITORING_TITLE_KEYWORDS = [
  "monitoring",
  "écho",
  "echo",
  "ultrasound",
  "échographie",
  "echographie",
  "blood",
  "prise de sang",
  "bloodwork",
  "scan",
];

const STIM_MED_LOOKAHEAD_HOURS = 12;

function dayIndex(start: string | null | undefined, today: string): number {
  if (!start) return 1;
  if (start > today) return 1;
  const a = new Date(`${start}T00:00:00`);
  const b = new Date(`${today}T00:00:00`);
  const diff = Math.floor((b.getTime() - a.getTime()) / 86_400_000);
  return Math.max(1, diff + 1);
}

function isMonitoringAppointment(appt: Appointment): boolean {
  const title = (appt.title ?? "").toLowerCase();
  return MONITORING_TITLE_KEYWORDS.some((kw) => title.includes(kw));
}

function isBetaAppointment(appt: Appointment): boolean {
  const searchable = [
    appt.title,
    appt.description,
    ...(appt.consultationType ?? []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return (
    (searchable.includes("beta") && searchable.includes("hcg")) ||
    searchable.includes("pregnancy blood")
  );
}

function findBetaAppointment(
  appointments: Appointment[],
  betaDate: string | null,
): Appointment | null {
  return (
    appointments
      .filter((appointment) => isBetaAppointment(appointment))
      .filter((appointment) => !betaDate || appointment.startDate === betaDate)
      .sort((a, b) =>
        (a.startTimeLocal ?? a.startTime ?? "").localeCompare(
          b.startTimeLocal ?? b.startTime ?? "",
        ),
      )[0] ?? null
  );
}

function findNextMonitoring(
  appointments: Appointment[],
  todayIso: string,
  horizonIso: string,
): Appointment | null {
  const future = appointments
    .filter((a) => !!a.startDate)
    .filter((a) => a.startDate! >= todayIso && a.startDate! <= horizonIso)
    .filter(isMonitoringAppointment)
    .sort((a, b) => {
      const dateCmp = (a.startDate ?? "").localeCompare(b.startDate ?? "");
      if (dateCmp !== 0) return dateCmp;
      return (a.startTime ?? "").localeCompare(b.startTime ?? "");
    });
  return future[0] ?? null;
}

interface NextMedDose {
  date: string;
  time: string;
}

function findNextMedDose(
  medications: PatientMedication[],
  now: Date,
): NextMedDose | null {
  const todayIso = toLocalDateString(now);
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowIso = toLocalDateString(tomorrow);
  const horizonMs = now.getTime() + STIM_MED_LOOKAHEAD_HOURS * 3_600_000;

  const candidates: { date: string; time: string; id: string }[] = [];

  for (const med of medications) {
    const { startDate, endDate, timeTaken, id } = med;
    if (!startDate || !timeTaken || timeTaken.length === 0) continue;
    if (startDate > tomorrowIso) continue;
    if (endDate && endDate < todayIso) continue;

    for (const slot of timeTaken) {
      const slot24 = normalizeTime(slot);
      if (!slot24) continue;
      for (const date of [todayIso, tomorrowIso]) {
        if (date < startDate) continue;
        if (endDate && date > endDate) continue;
        const at = new Date(`${date}T${slot24}:00`);
        if (Number.isNaN(at.getTime())) continue;
        if (at.getTime() <= now.getTime()) continue;
        if (at.getTime() > horizonMs) continue;
        candidates.push({ date, time: slot24, id });
      }
    }
  }

  candidates.sort((a, b) => {
    const dateCmp = a.date.localeCompare(b.date);
    if (dateCmp !== 0) return dateCmp;
    const timeCmp = a.time.localeCompare(b.time);
    if (timeCmp !== 0) return timeCmp;
    return a.id.localeCompare(b.id);
  });

  const first = candidates[0];
  return first ? { date: first.date, time: first.time } : null;
}

function normalizeTime(raw: string): string | null {
  // Accept "HH:MM" or "HH:MM:SS". Return "HH:MM".
  const match = /^([0-2]?\d):([0-5]\d)(?::\d\d)?$/.exec(raw.trim());
  if (!match) return null;
  const h = match[1]!.padStart(2, "0");
  return `${h}:${match[2]}`;
}

function hasFertilityMetric(phase: CycleHeaderPhase, type: string): boolean {
  const metrics = phase.fertilityMetrics;
  if (!Array.isArray(metrics)) return false;
  return metrics.some((m) => m?.type === type);
}

function dayInPhaseForStep(
  stepId: string | undefined,
  phases: CycleHeaderPhase[],
  cycle: CycleHeaderCycle,
  todayIso: string,
): number {
  if (!stepId) return 1;
  const phaseType = getCycleHeaderPhaseType(stepId);
  const phase = phaseType ? phases.find((p) => p.type === phaseType) : null;
  if (phase?.startDate) return dayIndex(phase.startDate, todayIso);
  return dayIndex(cycle.menstruationStartDate ?? cycle.startDate, todayIso);
}

function isHeaderStepComplete(
  stepId: string,
  stepIds: readonly string[],
  phases: CycleHeaderPhase[],
  todayIso: string,
): boolean {
  const phaseType = getCycleHeaderPhaseType(stepId);
  const phase = phaseType
    ? (phases.find((candidate) => candidate.type === phaseType) ?? null)
    : null;
  return isCycleStepComplete(stepId, stepIds, phase, todayIso);
}

/**
 * Resolve the active cycle's adaptive header state.
 *
 * The "current step" follows the same logic as the treatment hub
 * (`MoonTreatmentHubCard.currentStepId`): the first non-completed step in
 * the cycle-type-specific flow. That makes this resolver agree with the
 * hub even when phase rows are sparse — the hub only treats a phase as
 * "completed" when it has an `endDate`, and we mirror that.
 */
export function resolveCycleHeaderState(input: ResolveInput): CycleHeaderState {
  const { cycle, phases, appointments, medications, now } = input;
  if (!cycle) return { kind: "no_cycle" };

  const cycleType = cycle.cycleType;
  const todayIso = toLocalDateString(now);

  if (cycle.endDate && cycle.endDate < todayIso) {
    return { kind: "cycle_closed", cycleType };
  }

  // Two-week wait takes precedence over step-progress resolution. Once the
  // transfer has happened the luteal phase is open with a startDate (transfer
  // date) and, usually, an endDate = beta test date. `deriveStepProgress`
  // treats any phase carrying an endDate as "completed", so without this the
  // wait would fall through to `unknown_active` and the hero would drop its
  // day-post-transfer count and beta countdown. Guard on startDate ≤ today
  // (transfer done) and endDate not already in the past (beta not yet behind
  // us) so it doesn't fire before transfer or after the result window.
  // A recorded ending closes the wait even while the cycle is still open.
  // Recording a result no longer closes the cycle — that is a separate, deliberate
  // act now — so the phase dates alone can no longer be trusted to say whether the
  // patient is still waiting. Without this the hero goes on counting down to a
  // beta whose result is already saved.
  const hasRecordedEnding = !!cycle.outcome?.type;
  if (hasRecordedEnding) return { kind: "result_recorded", cycleType };

  const waitingLutealPhase =
    phases.find((p) => p.type === "luteal_phase") ?? null;
  if (
    waitingLutealPhase?.startDate &&
    waitingLutealPhase.startDate <= todayIso &&
    (!waitingLutealPhase.endDate || waitingLutealPhase.endDate >= todayIso)
  ) {
    const betaAppointment = findBetaAppointment(
      appointments,
      waitingLutealPhase.endDate ?? null,
    );
    const betaDate = waitingLutealPhase.endDate ?? betaAppointment?.startDate;
    return betaDate
      ? {
          kind: "beta_scheduled",
          cycleType,
          betaDate,
          betaTime:
            betaAppointment?.startTimeLocal ??
            betaAppointment?.startTime ??
            null,
        }
      : { kind: "beta_pending", cycleType };
  }

  const steps = getCycleHeaderStepIds(
    cycleType,
    cycle.protocol,
    phases,
    input.pgtPlan,
    input.cyclePlanSnapshot,
  );
  if (steps.length === 0) return { kind: "unknown_active", cycleType };

  // First step that has not been completed or explicitly skipped. This is the
  // same clinical rule as the treatment hub, kept here without importing the
  // React Native step component so this resolver remains pure and testable.
  const currentStepIndex = steps.findIndex(
    (stepId) => !isHeaderStepComplete(stepId, steps, phases, todayIso),
  );
  const currentStep =
    currentStepIndex >= 0 ? steps[currentStepIndex] : undefined;

  if (!currentStep) {
    // Every step completed but cycle not closed yet — sit on the last
    // meaningful state until the cycle is marked closed in the DB.
    return { kind: "unknown_active", cycleType };
  }

  const phaseByType = (type: string): CycleHeaderPhase | null =>
    phases.find((p) => p.type === type) ?? null;
  const protocolPhase = phaseByType("protocol");
  const retrievalPhase = phaseByType("egg_retrieval");
  const transferPhase = phaseByType("transfer");
  const lutealPhase = phaseByType("luteal_phase");

  switch (currentStep) {
    case "pre_treatment": {
      return {
        kind: "pre_treatment",
        cycleType,
        dayInPhase: dayInPhaseForStep(currentStep, phases, cycle, todayIso),
      };
    }

    case "protocol":
    case "stimulation": {
      const dayInPhase = dayInPhaseForStep(
        currentStep,
        phases,
        cycle,
        todayIso,
      );
      const triggerDate = protocolPhase?.triggerShot?.date ?? null;
      const triggerTime = protocolPhase?.triggerShot?.time ?? null;
      if (triggerDate && !retrievalPhase?.startDate) {
        return {
          kind: "stim_trigger_logged",
          cycleType,
          triggerDate,
          triggerTime,
        };
      }
      const horizon = new Date(now);
      horizon.setDate(horizon.getDate() + 14);
      const monitoring = findNextMonitoring(
        appointments,
        todayIso,
        toLocalDateString(horizon),
      );
      if (monitoring && monitoring.startDate) {
        return {
          kind: "stim_monitoring_next",
          cycleType,
          dayInPhase,
          apptDate: monitoring.startDate,
          apptTime: monitoring.startTime ?? null,
        };
      }
      const nextDose = findNextMedDose(medications, now);
      if (nextDose) {
        return {
          kind: "stim_med_next",
          cycleType,
          dayInPhase,
          medDate: nextDose.date,
          medTime: nextDose.time,
        };
      }
      return { kind: "stim_generic", cycleType, dayInPhase };
    }

    case "trigger_shot": {
      const triggerDate = protocolPhase?.triggerShot?.date ?? null;
      const triggerTime = protocolPhase?.triggerShot?.time ?? null;
      if (triggerDate) {
        // Trigger fired but the user hasn't marked the step complete yet
        // (or retrieval scheduling lives elsewhere). Same copy as stim flow.
        return {
          kind: "stim_trigger_logged",
          cycleType,
          triggerDate,
          triggerTime,
        };
      }
      // Trigger step is the next thing to do but no data yet — treat as
      // late-stage stim so the user sees the cycle isn't stalled.
      return {
        kind: "stim_generic",
        cycleType,
        dayInPhase: dayInPhaseForStep(currentStep, phases, cycle, todayIso),
      };
    }

    case "egg_retrieval":
    case "freezing": {
      if (retrievalPhase?.startDate && retrievalPhase.startDate > todayIso) {
        return {
          kind: "retrieval_scheduled",
          cycleType,
          retrievalDate: retrievalPhase.startDate,
        };
      }
      if (
        retrievalPhase?.startDate &&
        retrievalPhase.startDate <= todayIso &&
        !hasFertilityMetric(retrievalPhase, "retrieved_oocytes")
      ) {
        return {
          kind: "retrieval_done_pending",
          cycleType,
          retrievalDate: retrievalPhase.startDate,
        };
      }
      // Retrieval is the next thing in the journey but no date on file.
      return {
        kind: "retrieval_done_pending",
        cycleType,
        retrievalDate: null,
      };
    }

    case "fertilization":
    case "insemination": {
      return { kind: "fertilization", cycleType };
    }

    case "embryo_development": {
      return {
        kind: "embryo_dev",
        cycleType,
        dayInPhase: dayInPhaseForStep(currentStep, phases, cycle, todayIso),
      };
    }

    case "transfer": {
      if (transferPhase?.startDate) {
        return {
          kind: "transfer_scheduled",
          cycleType,
          transferDate: transferPhase.startDate,
        };
      }
      return { kind: "unknown_active", cycleType };
    }

    case "luteal_phase": {
      const phase = lutealPhase;
      const betaAppointment = findBetaAppointment(
        appointments,
        phase?.endDate ?? null,
      );
      const betaDate = phase?.endDate ?? betaAppointment?.startDate ?? null;
      if (betaDate) {
        return {
          kind: "beta_scheduled",
          cycleType,
          betaDate,
          betaTime:
            betaAppointment?.startTimeLocal ??
            betaAppointment?.startTime ??
            null,
        };
      }
      return { kind: "beta_pending", cycleType };
    }

    default:
      return { kind: "unknown_active", cycleType };
  }
}
